// §4.13 Planning settings (`/planning/settings`): the facility's §2.22 row.
// `planning.settings.manage` edits it; the two MPS approval bars move only for
// the group_admin lineage (D-351 ruling 8). A facility whose row was never
// written reads the defaults, and its first save creates the row. The facility
// calendar (P3-H-1) is picked from the active shift patterns by a reader who
// may read them (`planning.calendar.read`); any other reader is told only
// whether the facility has one, since the pattern list is closed to them.
// P3-H-2 adds MRP's release buffer and quantity tolerance and the nightly MRP
// watermark, read-only. The agent firm cap P3-H-2 also put here moved to the
// planner agent's policy in P3-K (D-526 ruling 2). P3-I-2 adds the
// schedule's horizon, batching window, solve debounce, frozen fence and
// auto-publish switch, and the nightly schedule watermark, read-only (§2.22,
// §4.13, D-412 ruling 13).

import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Card } from "../../../../components/card";
import { Field } from "../../../../components/field";
import { Select } from "../../../../components/select";
import { CardSkeleton, ErrorState } from "../../../../components/states";
import { Banner } from "../../../../components/toast";
import { formatBusinessDate } from "../../../../lib/datetime";
import { UnauthorizedError } from "../../../../lib/problem";
import { RequestError, requestIdOf } from "../../../../lib/request";
import { useShiftPatterns } from "../hooks/calendar-queries";
import { usePlanningPermissions } from "../hooks/permissions";
import {
  problemCode,
  problemExtra,
  putPlanningSettings,
  usePlanningRefresh,
  usePlanningSettings,
  type PlanningSettings,
} from "../hooks/planning-queries";
import { parsePct, parseWhole, useOffline } from "../lib/planning-lib";
import { PlanningHeader } from "../components/planning-header";
import { usePlanningFacility } from "../planning-layout";

type BaseField =
  | "mps_auto_approve_delta_pct"
  | "mps_facility_admin_delta_pct"
  | "horizon_weeks"
  | "demand_refresh_debounce_minutes"
  | "ctp_safety_margin_days"
  | "promise_ontime_downgrade_pct";

/** P3-H-2's fields (§4.13, D-388 ruling 9). */
type MrpField = "mrp_release_buffer_days" | "mrp_qty_tolerance_pct";

/** P3-I-2's numeric schedule fields (§2.22); the auto-publish switch is held apart. */
type ScheduleField =
  | "schedule_horizon_days"
  | "schedule_batching_window_hours"
  | "schedule_solve_debounce_minutes"
  | "schedule_frozen_hours"
  | "overtime_cost_pct";

type FieldName = BaseField | MrpField | ScheduleField;

/** The form as text; the later fields are optional so a G-1 form still checks. */
type Form = Record<BaseField, string> & Partial<Record<MrpField | ScheduleField, string>>;

/** Each schedule field's whole-number range, the column's CHECK (§2.22). */
const SCHEDULE_RANGE: Readonly<Record<ScheduleField, readonly [number, number]>> = {
  schedule_horizon_days: [7, 84],
  schedule_batching_window_hours: [1, 336],
  schedule_solve_debounce_minutes: [1, 60],
  schedule_frozen_hours: [0, 72],
  overtime_cost_pct: [100, 300],
};

const FIELDS: readonly FieldName[] = [
  "mps_auto_approve_delta_pct",
  "mps_facility_admin_delta_pct",
  "horizon_weeks",
  "demand_refresh_debounce_minutes",
  "ctp_safety_margin_days",
  "promise_ontime_downgrade_pct",
  "mrp_release_buffer_days",
  "mrp_qty_tolerance_pct",
  "schedule_horizon_days",
  "schedule_batching_window_hours",
  "schedule_solve_debounce_minutes",
  "schedule_frozen_hours",
  "overtime_cost_pct",
];

// The line a 400 on each field shows.
const FIELD_400: Readonly<Record<FieldName, string>> = {
  mps_auto_approve_delta_pct: "planning.settings.errors.pct",
  mps_facility_admin_delta_pct: "planning.settings.errors.pct",
  horizon_weeks: "planning.settings.errors.horizon",
  demand_refresh_debounce_minutes: "planning.settings.errors.debounce",
  ctp_safety_margin_days: "planning.settings.errors.days",
  promise_ontime_downgrade_pct: "planning.settings.errors.pct",
  mrp_release_buffer_days: "planning.settings.errors.buffer",
  mrp_qty_tolerance_pct: "planning.settings.errors.pctMax",
  schedule_horizon_days: "planning.settings.errors.scheduleHorizon",
  schedule_batching_window_hours: "planning.settings.errors.batching",
  schedule_solve_debounce_minutes: "planning.settings.errors.debounce",
  schedule_frozen_hours: "planning.settings.errors.frozen",
  overtime_cost_pct: "planning.settings.errors.overtime",
};

const BARS: ReadonlySet<FieldName> = new Set([
  "mps_auto_approve_delta_pct",
  "mps_facility_admin_delta_pct",
]);

/**
 * The two delivery-promise fields are not drawn until P3-J's promises read them
 * (§4.13, D-425 ruling 1). A save still sends their stored values, and a 400 that
 * names one of them reads as the save failure rather than a line on a field nobody sees.
 */
const UNDRAWN: ReadonlySet<FieldName> = new Set([
  "ctp_safety_margin_days",
  "promise_ontime_downgrade_pct",
]);

function formOf(row: PlanningSettings): Form {
  return {
    mps_auto_approve_delta_pct: String(Number(row.mps_auto_approve_delta_pct)),
    mps_facility_admin_delta_pct: String(Number(row.mps_facility_admin_delta_pct)),
    horizon_weeks: String(row.horizon_weeks),
    demand_refresh_debounce_minutes: String(row.demand_refresh_debounce_minutes),
    ctp_safety_margin_days: String(row.ctp_safety_margin_days),
    promise_ontime_downgrade_pct: String(Number(row.promise_ontime_downgrade_pct)),
    mrp_release_buffer_days: String(row.mrp_release_buffer_days),
    mrp_qty_tolerance_pct: String(Number(row.mrp_qty_tolerance_pct)),
    schedule_horizon_days: String(row.schedule_horizon_days),
    schedule_batching_window_hours: String(row.schedule_batching_window_hours),
    schedule_solve_debounce_minutes: String(row.schedule_solve_debounce_minutes),
    schedule_frozen_hours: String(row.schedule_frozen_hours),
    overtime_cost_pct: String(row.overtime_cost_pct),
  };
}

type Problems = Partial<Record<FieldName, string>>;

/** Checks the form as the server will, so the common mistake never makes the trip. */
export function checkSettingsForm(form: Form): Problems {
  const problems: Problems = {};
  for (const name of ["mps_auto_approve_delta_pct", "mps_facility_admin_delta_pct"] as const) {
    if (parsePct(form[name]) === undefined) problems[name] = "planning.settings.errors.pct";
  }
  const downgrade = parsePct(form.promise_ontime_downgrade_pct);
  if (downgrade === undefined || Number(downgrade) > 100) {
    problems.promise_ontime_downgrade_pct = "planning.settings.errors.pctMax";
  }
  if (parseWhole(form.horizon_weeks, 1, 52) === undefined)
    problems.horizon_weeks = "planning.settings.errors.horizon";
  if (parseWhole(form.demand_refresh_debounce_minutes, 1, 60) === undefined) {
    problems.demand_refresh_debounce_minutes = "planning.settings.errors.debounce";
  }
  if (parseWhole(form.ctp_safety_margin_days, 0, 32767) === undefined) {
    problems.ctp_safety_margin_days = "planning.settings.errors.days";
  }
  if (
    form.mrp_release_buffer_days !== undefined &&
    parseWhole(form.mrp_release_buffer_days, 0, 10) === undefined
  ) {
    problems.mrp_release_buffer_days = "planning.settings.errors.buffer";
  }
  if (form.mrp_qty_tolerance_pct !== undefined) {
    const tolerance = parsePct(form.mrp_qty_tolerance_pct);
    if (tolerance === undefined || Number(tolerance) > 100) {
      problems.mrp_qty_tolerance_pct = "planning.settings.errors.pctMax";
    }
  }
  for (const [name, [min, max]] of Object.entries(SCHEDULE_RANGE) as [
    ScheduleField,
    readonly [number, number],
  ][]) {
    const text = form[name];
    if (text !== undefined && parseWhole(text, min, max) === undefined) {
      problems[name] = FIELD_400[name];
    }
  }
  const auto = parsePct(form.mps_auto_approve_delta_pct);
  const fa = parsePct(form.mps_facility_admin_delta_pct);
  if (auto !== undefined && fa !== undefined && Number(auto) > Number(fa)) {
    problems.mps_auto_approve_delta_pct = "planning.settings.errors.barOrder";
    problems.mps_facility_admin_delta_pct = "planning.settings.errors.barOrder";
  }
  return problems;
}

export function PlanningSettingsScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { facility } = usePlanningFacility();
  const perms = usePlanningPermissions();
  const offline = useOffline();
  const refresh = usePlanningRefresh();
  const settings = usePlanningSettings(facility.id);
  const canEdit = perms.has("planning.settings.manage");
  const canEditBars = canEdit && perms.isGroupAdmin;
  const canReadCalendars = perms.has("planning.calendar.read");
  const patterns = useShiftPatterns(canReadCalendars);

  const [form, setForm] = useState<Form | undefined>(undefined);
  const [loadedFor, setLoadedFor] = useState<string | undefined>(undefined);
  // The row the form was last filled from, which the next save names as expected.
  const [current, setCurrent] = useState<PlanningSettings | undefined>(undefined);
  const [problems, setProblems] = useState<Problems>({});
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<"saved" | "changed" | undefined>(undefined);
  const [failure, setFailure] = useState<unknown>(undefined);
  // The facility calendar's pattern id as the select holds it, "" for none.
  const [calendarChoice, setCalendarChoice] = useState("");
  const [calendarProblem, setCalendarProblem] = useState<string | undefined>(undefined);
  // The schedule's auto-publish switch, held apart from the text fields.
  const [autoPublish, setAutoPublish] = useState(true);

  // The form takes each new row the read answers; a save or a conflict sets it
  // directly, and the refetch that follows brings the same row.
  const read = settings.data;
  const stamp =
    read !== undefined ? `${read.facility_id}|${read.updated_at ?? "unwritten"}` : undefined;
  useEffect(() => {
    if (read !== undefined && stamp !== loadedFor) {
      setForm(formOf(read));
      setCurrent(read);
      setCalendarChoice(read.facility_shift_pattern_id ?? "");
      setAutoPublish(read.schedule_auto_publish);
      setLoadedFor(stamp);
      setProblems({});
      setCalendarProblem(undefined);
    }
  }, [read, stamp, loadedFor]);
  const row =
    read === undefined ? undefined : current?.facility_id === read.facility_id ? current : read;

  useEffect(() => {
    if (settings.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [settings.error, navigate]);

  const head = <PlanningHeader title={t("planning.settings.title")} />;

  if (settings.error !== null) {
    return (
      <section className="planning-screen" data-testid="planning-settings">
        {head}
        <ErrorState
          title={t("planning.settings.loadError")}
          retryLabel={t("planning.common.retry")}
          onRetry={() => void settings.refetch()}
          requestId={requestIdOf(settings.error)}
          requestIdLabel={t("planning.common.requestId")}
        />
      </section>
    );
  }
  if (row === undefined || form === undefined) {
    return (
      <section className="planning-screen" data-testid="planning-settings">
        {head}
        <CardSkeleton lines={6} label={t("planning.settings.loading")} />
      </section>
    );
  }

  const set = (name: FieldName, value: string) => {
    setForm({ ...form, [name]: value });
    setProblems((p) => ({ ...p, [name]: undefined }));
    setNotice(undefined);
  };

  const onSave = async () => {
    const found = checkSettingsForm(form);
    setProblems(found);
    setFailure(undefined);
    setCalendarProblem(undefined);
    if (Object.keys(found).length > 0) return;
    // The calendar is sent only when it moved, so a reader who cannot list the
    // patterns never sends one and a stored pattern is never re-checked.
    const calendarMoved =
      canReadCalendars && calendarChoice !== (row.facility_shift_pattern_id ?? "");
    setSaving(true);
    try {
      const saved = await putPlanningSettings(facility.id, {
        mps_auto_approve_delta_pct: parsePct(form.mps_auto_approve_delta_pct) ?? "0",
        mps_facility_admin_delta_pct: parsePct(form.mps_facility_admin_delta_pct) ?? "0",
        horizon_weeks: Number(form.horizon_weeks),
        demand_refresh_debounce_minutes: Number(form.demand_refresh_debounce_minutes),
        ctp_safety_margin_days: Number(form.ctp_safety_margin_days),
        promise_ontime_downgrade_pct: parsePct(form.promise_ontime_downgrade_pct) ?? "0",
        mrp_release_buffer_days: Number(form.mrp_release_buffer_days),
        mrp_qty_tolerance_pct: parsePct(form.mrp_qty_tolerance_pct ?? "") ?? "0",
        ...(calendarMoved
          ? { facility_shift_pattern_id: calendarChoice === "" ? null : calendarChoice }
          : {}),
        schedule_horizon_days: Number(form.schedule_horizon_days),
        schedule_batching_window_hours: Number(form.schedule_batching_window_hours),
        schedule_solve_debounce_minutes: Number(form.schedule_solve_debounce_minutes),
        schedule_frozen_hours: Number(form.schedule_frozen_hours),
        overtime_cost_pct: Number(form.overtime_cost_pct),
        schedule_auto_publish: autoPublish,
        expected_updated_at: row.updated_at,
      });
      setForm(formOf(saved));
      setCurrent(saved);
      setCalendarChoice(saved.facility_shift_pattern_id ?? "");
      setAutoPublish(saved.schedule_auto_publish);
      setNotice("saved");
      void refresh.settings(facility.id);
    } catch (error) {
      if (error instanceof UnauthorizedError) {
        void navigate({ to: "/login" });
        return;
      }
      const code = problemCode(error);
      if (code === "SHIFT_PATTERN_NOT_FOUND") {
        setCalendarProblem("planning.settings.errors.pattern");
      } else if (code === "PLANNING_SETTINGS_CHANGED") {
        const stored = problemExtra(error, "current") as PlanningSettings | undefined;
        if (stored !== undefined) {
          setForm(formOf(stored));
          setCurrent(stored);
          setCalendarChoice(stored.facility_shift_pattern_id ?? "");
          setAutoPublish(stored.schedule_auto_publish);
        }
        setNotice("changed");
        void refresh.settings(facility.id);
      } else if (error instanceof RequestError && error.httpStatus === 403) {
        const fields = (problemExtra(error, "fields") as FieldName[] | undefined) ?? [];
        if (fields.length > 0) {
          const next: Problems = {};
          for (const f of fields) next[f] = "planning.settings.errors.barsLineage";
          setProblems(next);
        } else setFailure(error);
      } else if (
        error instanceof RequestError &&
        error.httpStatus === 400 &&
        error.fieldErrors.length > 0
      ) {
        const next: Problems = {};
        for (const fe of error.fieldErrors) {
          const name = FIELDS.find((f) => fe.path === f || fe.path.endsWith(`.${f}`));
          if (name !== undefined && !UNDRAWN.has(name)) next[name] = FIELD_400[name];
        }
        if (fields400Bars(error)) {
          next.mps_auto_approve_delta_pct = "planning.settings.errors.barOrder";
          next.mps_facility_admin_delta_pct = "planning.settings.errors.barOrder";
        }
        setProblems(next);
        if (Object.keys(next).length === 0) setFailure(error);
      } else setFailure(error);
    } finally {
      setSaving(false);
    }
  };

  const field = (
    name: FieldName,
    opts: { suffix: string; hint?: string; disabledReason?: string | undefined },
  ) => {
    const disabled = !canEdit || offline || saving || (BARS.has(name) && !canEditBars);
    const problem = problems[name];
    return (
      <div className="planning-field" key={name}>
        <Field label={t(`planning.settings.fields.${name}`)}>
          <input
            type="text"
            inputMode={name.endsWith("_pct") ? "decimal" : "numeric"}
            value={form[name] ?? ""}
            disabled={disabled}
            aria-invalid={problem !== undefined || undefined}
            data-testid={`planning-settings-${name}`}
            onChange={(e) => {
              set(name, e.target.value);
            }}
          />
        </Field>
        <span className="planning-hint">{opts.suffix}</span>
        {opts.hint !== undefined ? <span className="planning-hint">{opts.hint}</span> : null}
        {opts.disabledReason !== undefined ? (
          <span className="planning-hint">{opts.disabledReason}</span>
        ) : null}
        {problem !== undefined ? (
          <span
            className="field-error"
            role="alert"
            data-testid="planning-settings-error"
            data-field={name}
          >
            {t(problem)}
          </span>
        ) : null}
      </div>
    );
  };

  const barsReason =
    canEdit && !perms.isGroupAdmin ? t("planning.settings.barsGroupAdmin") : undefined;

  return (
    <section className="planning-screen" data-testid="planning-settings">
      <PlanningHeader
        title={t("planning.settings.title")}
        actions={
          canEdit ? (
            <Button
              variant="primary"
              busy={saving}
              busyLabel={t("planning.common.saving")}
              disabled={offline}
              data-testid="planning-settings-save"
              onClick={() => void onSave()}
            >
              {t("planning.common.save")}
            </Button>
          ) : undefined
        }
      />
      {!canEdit ? (
        <p className="planning-note" data-testid="planning-settings-read-only">
          {t("planning.settings.readOnly")}
        </p>
      ) : null}
      {canEdit && offline ? (
        <p className="planning-note" role="status">
          {t("planning.common.offlineWrites")}
        </p>
      ) : null}
      {row.updated_at === null ? (
        <p className="planning-note" data-testid="planning-settings-defaults">
          {t("planning.settings.defaults")}
        </p>
      ) : null}
      {notice === "saved" ? (
        <Banner kind="ok" data-testid="planning-settings-saved">
          {t("planning.settings.saved")}
        </Banner>
      ) : null}
      {notice === "changed" ? (
        <Banner kind="warn" data-testid="planning-settings-changed">
          {t("planning.common.changedBySomeone")}
        </Banner>
      ) : null}
      {failure !== undefined ? (
        <Banner kind="bad" data-testid="planning-settings-failure">
          {t("planning.common.saveFailed", { requestId: requestIdOf(failure) ?? "-" })}
        </Banner>
      ) : null}
      <Card title={t("planning.settings.mpsTitle")}>
        <p className="planning-hint">{t("planning.settings.mpsBody")}</p>
        <div className="planning-form">
          {field("mps_auto_approve_delta_pct", { suffix: "%", disabledReason: barsReason })}
          {field("mps_facility_admin_delta_pct", { suffix: "%", disabledReason: barsReason })}
        </div>
      </Card>
      <Card title={t("planning.settings.horizonTitle")}>
        <div className="planning-form">
          {field("horizon_weeks", {
            suffix: t("planning.settings.weeks"),
            hint: t("planning.settings.horizonHint"),
          })}
          {field("demand_refresh_debounce_minutes", {
            suffix: t("planning.settings.minutes"),
            hint: t("planning.settings.debounceHint"),
          })}
        </div>
        <p className="planning-hint" data-testid="planning-settings-nightly">
          {row.nightly_refreshed_on === null
            ? t("planning.settings.nightlyNever")
            : t("planning.settings.nightlyOn", {
                date: formatBusinessDate(row.nightly_refreshed_on, i18n.language),
              })}
        </p>
        <p className="planning-hint" data-testid="planning-settings-weekly">
          {row.weekly_proposed_on === null
            ? t("planning.settings.weeklyNever")
            : t("planning.settings.weeklyOn", {
                date: formatBusinessDate(row.weekly_proposed_on, i18n.language),
              })}
        </p>
      </Card>
      <Card title={t("planning.settings.calendarTitle")} data-testid="planning-settings-calendar">
        <p className="planning-hint">{t("planning.settings.calendarHint")}</p>
        {canReadCalendars ? (
          <CalendarPicker
            value={calendarChoice}
            storedId={row.facility_shift_pattern_id}
            patterns={patterns}
            disabled={!canEdit || offline || saving}
            problem={calendarProblem}
            onChange={(value) => {
              setCalendarChoice(value);
              setCalendarProblem(undefined);
              setNotice(undefined);
            }}
          />
        ) : (
          <p className="planning-last" data-testid="planning-settings-calendar-state">
            {row.facility_shift_pattern_id === null
              ? t("planning.settings.calendarUnset")
              : t("planning.settings.calendarSet")}
          </p>
        )}
      </Card>
      <Card title={t("planning.settings.mrpTitle")} data-testid="planning-settings-mrp">
        <p className="planning-hint">{t("planning.settings.mrpBody")}</p>
        <div className="planning-form">
          {field("mrp_release_buffer_days", {
            suffix: t("planning.settings.workingDays"),
            hint: t("planning.settings.bufferHint"),
          })}
          {field("mrp_qty_tolerance_pct", {
            suffix: "%",
            hint: t("planning.settings.toleranceHint"),
          })}
        </div>
        <p className="planning-hint" data-testid="planning-settings-nightly-mrp">
          {row.nightly_mrp_on === null
            ? t("planning.settings.nightlyMrpNever")
            : t("planning.settings.nightlyMrpOn", {
                date: formatBusinessDate(row.nightly_mrp_on, i18n.language),
              })}
        </p>
      </Card>
      <Card title={t("planning.settings.scheduleTitle")} data-testid="planning-settings-schedule">
        <p className="planning-hint">{t("planning.settings.scheduleBody")}</p>
        <div className="planning-form">
          {field("schedule_horizon_days", {
            suffix: t("planning.settings.days"),
            hint: t("planning.settings.scheduleHorizonHint"),
          })}
          {field("schedule_frozen_hours", {
            suffix: t("planning.settings.hours"),
            hint: t("planning.settings.frozenHint"),
          })}
          {field("schedule_batching_window_hours", {
            suffix: t("planning.settings.hours"),
            hint: t("planning.settings.batchingHint"),
          })}
          {field("schedule_solve_debounce_minutes", {
            suffix: t("planning.settings.minutes"),
            hint: t("planning.settings.solveDebounceHint"),
          })}
          {field("overtime_cost_pct", {
            suffix: "%",
            hint: t("planning.settings.overtimeCostHint"),
          })}
        </div>
        <label className="planning-toggle">
          <input
            type="checkbox"
            checked={autoPublish}
            disabled={!canEdit || offline || saving}
            data-testid="planning-settings-schedule_auto_publish"
            onChange={(e) => {
              setAutoPublish(e.target.checked);
              setNotice(undefined);
            }}
          />
          <span>{t("planning.settings.fields.schedule_auto_publish")}</span>
        </label>
        <p className="planning-hint">{t("planning.settings.autoPublishHint")}</p>
        <p className="planning-hint" data-testid="planning-settings-nightly-schedule">
          {row.nightly_solved_on === null
            ? t("planning.settings.nightlyScheduleNever")
            : t("planning.settings.nightlyScheduleOn", {
                date: formatBusinessDate(row.nightly_solved_on, i18n.language),
              })}
        </p>
      </Card>
    </section>
  );
}

/** The facility calendar select: no calendar, then the active patterns. */
function CalendarPicker({
  value,
  storedId,
  patterns,
  disabled,
  problem,
  onChange,
}: {
  value: string;
  storedId: string | null;
  patterns: ReturnType<typeof useShiftPatterns>;
  disabled: boolean;
  problem: string | undefined;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation();
  if (patterns.error !== null) {
    return (
      <p className="planning-note" role="alert" data-testid="planning-settings-calendar-error">
        {t("planning.settings.patternsError")}
      </p>
    );
  }
  if (patterns.data === undefined) {
    return (
      <p className="planning-hint" data-testid="planning-settings-calendar-loading">
        {t("planning.settings.patternsLoading")}
      </p>
    );
  }
  // The stored pattern is offered even if it left the active list, so the
  // select never shows a value it cannot name.
  const options = patterns.data.filter((p) => p.status === "active" || p.id === storedId);
  return (
    <div className="planning-field">
      <Field label={t("planning.settings.fields.facility_shift_pattern_id")}>
        <Select
          value={value}
          disabled={disabled}
          aria-invalid={problem !== undefined || undefined}
          data-testid="planning-settings-facility_shift_pattern_id"
          onChange={(e) => {
            onChange(e.target.value);
          }}
        >
          <option value="">{t("planning.settings.noCalendar")}</option>
          {options.map((p) => (
            <option key={p.id} value={p.id}>
              {p.status === "active"
                ? p.name
                : t("planning.settings.archivedPattern", { name: p.name })}
            </option>
          ))}
        </Select>
      </Field>
      {problem !== undefined ? (
        <span
          className="field-error"
          role="alert"
          data-testid="planning-settings-error"
          data-field="facility_shift_pattern_id"
        >
          {t(problem)}
        </span>
      ) : null}
    </div>
  );
}

// A 400 whose issue names the bar-order rule on both bars.
function fields400Bars(error: RequestError): boolean {
  return error.fieldErrors.some(
    (fe) =>
      (fe.params as Record<string, unknown> | undefined)?.["reason"] ===
        "auto_above_facility_admin" || fe.code === "auto_above_facility_admin",
  );
}
